import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import * as Icons from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { useNotifications } from "@/context/NotificationsContext";
import { DateRangeFilter, initialRange, useRange } from "@/components/shared/DateRangeFilter";
import { inRange } from "@/lib/dateRange";
import { text, fmtTs, toDate } from "@/lib/format";

const last10 = (v) => String(v || "").replace(/\D/g, "").slice(-10);
const OUTCOME = {
  otp_sent: "bg-sky-50 text-sky-700 border-sky-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
};

// Brugo Pro login attempts (pro_logins), matched to registered professionals by phone.
export function LoginAttempts({ pros, onOpen }) {
  const logs = useCollection("pro_logins");
  const { isUnseen } = useNotifications();
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const [dr, setDr] = useState(initialRange("attempted_at"));
  const range = useRange(dr);
  const proBy = useMemo(() => Object.fromEntries(pros.filter((p) => p.phone).map((p) => [last10(p.phone), p])), [pros]);
  const rows = useMemo(() => logs.map((l) => ({ ...l, pro: proBy[last10(l.phone)] }))
    .sort((a, b) => (toDate(b.attempted_at)?.getTime() || 0) - (toDate(a.attempted_at)?.getTime() || 0)), [logs, proBy]);
  const shown = rows.filter((l) => inRange(l.attempted_at, range)
    && (only === "all" || (only === "unregistered" ? !l.pro : l.outcome === only))
    && (!q || JSON.stringify(l).toLowerCase().includes(q.toLowerCase())));
  const filters = [["all", "All"], ["otp_sent", "OTP sent"], ["success", "Success"], ["failed", "Failed"], ["unregistered", "Not registered"]];
  return (
    <Card data-testid="login-attempts">
      <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
        <div className="relative w-60">
          <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input data-testid="logins-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search phone, device…" className="h-9 pl-8 text-xs" />
        </div>
        <DateRangeFilter value={dr} onChange={setDr} testId="logins-date-filter" />
        <div className="flex flex-wrap gap-1.5">
          {filters.map(([k, l]) => (
            <button key={k} onClick={() => setOnly(k)} data-testid={`logins-filter-${k}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${only === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
        <span className="ml-auto text-xs font-mono text-muted-foreground" data-testid="logins-count">{shown.length} attempts</span>
      </div>
      <div className="overflow-x-auto"><Table>
        <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Phone</TableHead><TableHead>Outcome</TableHead><TableHead>Professional</TableHead><TableHead>Device</TableHead><TableHead>App version</TableHead></TableRow></TableHeader>
        <TableBody>
          {shown.map((l) => {
            const unseen = isUnseen({ key: `pro_logins/${l.id}`, cat: "pros", ts: toDate(l.attempted_at)?.getTime() || 0 });
            return (
              <TableRow key={l.id} data-testid={`login-row-${l.id}`}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{unseen && <span className="inline-block h-2 w-2 rounded-full bg-red-600 mr-1.5" data-testid={`login-new-${l.id}`} />}{fmtTs(l.attempted_at)}</TableCell>
                <TableCell className="font-mono text-sm">{text(l.phone)}</TableCell>
                <TableCell><span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${OUTCOME[l.outcome] || "bg-muted text-muted-foreground border-border"}`}>{text(l.outcome)?.replace("_", " ")}</span>{l.failure_reason && <span className="block text-[10px] text-muted-foreground max-w-[180px] truncate">{l.failure_reason}</span>}</TableCell>
                <TableCell className="text-sm">{l.pro
                  ? <button className="text-primary hover:underline" onClick={() => onOpen(l.pro.id)} data-testid={`login-pro-${l.id}`}>{text(l.pro.full_name || l.pro.business_name)}</button>
                  : <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`login-unregistered-${l.id}`}>Not registered</span>}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate" title={l.device || ""}>{text(l.device)}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{text(l.app_version)}</TableCell>
              </TableRow>
            );
          })}
          {shown.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground" data-testid="logins-empty">No login attempts yet — they appear here live as soon as Brugo Pro starts writing to pro_logins.</TableCell></TableRow>}
        </TableBody>
      </Table></div>
    </Card>
  );
}
